'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { usePageLocaleContext } from '@/app/context/PageLocaleContext';
import { useCustomerAuthContext } from '@/app/context/CustomerAuthContext';
import type { ResolvedDefaultImages } from '@/components/common/SafeImage';
import type { HeaderCategoryMenuModel } from '@/lib/header-category-menu';
import { buildLanguageHref } from '@/lib/language-switch';
import { createTranslator } from '@/lib/localization';
import { categoriesIndexHref, homeHref, menuItemHref, type MenuItemLike } from '@/lib/storefront-links';
import { Theme03CategoryAccordion, Theme03CategoryDropdown } from '@/themes/theme03/Theme03CategoryMenu';

type MenuItem = MenuItemLike & { label: string };

type Theme03HeaderProps = {
  corporateInfo?: { companyName?: string; logoUrl?: string; companyLogoUrl?: string } | null;
  headerMainMenu?: { items: MenuItem[] } | null;
  /** The domain's active header category menu, already resolved by Storefront Core (null = none: nothing is rendered). */
  categoryMenu?: HeaderCategoryMenuModel | null;
  /** The domain's default images (Core), the fallback for a category without its own image. */
  defaultImages?: ResolvedDefaultImages;
  texts?: Record<string, string> | null;
  activeLanguage?: string;
};

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'tr', label: 'TR' },
];

/**
 * Marble.Theme.03 header: a quiet, single-row premium bar — logo, primary navigation, language, account.
 *
 * Compared with the legacy header this theme presents NO search box, favorites, cart or "premium design" tagline: the
 * cart and favorites have no add-to-cart / add-to-favorites flow in the storefront (the underlying state and modals
 * are untouched and still available to the other themes), and the tagline is decoration. Mobile keeps the full
 * navigation, the language switch and the account link inside one disclosure panel.
 */
export function Theme03Header({ corporateInfo, headerMainMenu, categoryMenu, defaultImages = {}, texts, activeLanguage = 'en' }: Theme03HeaderProps) {
  const t = createTranslator(texts ?? {});
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { pageLocale } = usePageLocaleContext();
  const { isAuthenticated } = useCustomerAuthContext();

  const logoSrc = corporateInfo?.logoUrl || corporateInfo?.companyLogoUrl;
  const companyName = corporateInfo?.companyName || '';
  const langPrefix = activeLanguage !== 'en' ? `/${activeLanguage}` : '';

  // The admin's main menu; with none configured the only entry every domain can honestly offer is the category index.
  const items: MenuItem[] = headerMainMenu?.items?.length
    ? headerMainMenu.items
    : [{ kind: 'custom', label: t('header.categories'), url: categoriesIndexHref(activeLanguage) }];

  const languageHref = (language: string) =>
    buildLanguageHref(language, {
      pathname,
      query: searchParams.toString(),
      hash: typeof window !== 'undefined' ? window.location.hash : '',
      pageLocale,
    });

  const accountHref = `${langPrefix}${isAuthenticated ? '/my-account' : '/login'}`;

  const languageSwitch = (
    <span className="t3-lang" role="group" aria-label="Language">
      {LANGUAGES.map((language) => (
        <Link
          key={language.code}
          href={languageHref(language.code)}
          hrefLang={language.code}
          aria-current={language.code === activeLanguage ? 'true' : undefined}
          onClick={() => setOpen(false)}
        >
          {language.label}
        </Link>
      ))}
    </span>
  );

  return (
    <header className="t3-header">
      <div className="theme-shell">
        <div className="t3-header__bar">
          <Link href={homeHref(activeLanguage)} className="t3-logo" aria-label={companyName || undefined}>
            {logoSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={logoSrc} alt={companyName} />
            ) : (
              <span>{companyName}</span>
            )}
          </Link>

          <nav className="t3-nav" aria-label={t('header.categories')}>
            <ul>
              {categoryMenu ? <Theme03CategoryDropdown menu={categoryMenu} label={t('header.allCategories')} defaults={defaultImages} /> : null}
              {items.map((item, index) => (
                <li key={`${item.label}-${index}`}>
                  <Link href={menuItemHref(item, activeLanguage)}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="t3-header__tools">
            {languageSwitch}
            <Link href={accountHref} className="hidden lg:inline-block">
              {t('header.account')}
            </Link>
            <button
              type="button"
              className="t3-burger"
              aria-expanded={open}
              aria-controls="t3-mobile-panel"
              aria-label={t('header.toggleMenu')}
              onClick={() => setOpen((value) => !value)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                {open ? <path d="M4 4l12 12M16 4L4 16" /> : <path d="M3 6h14M3 10h14M3 14h14" />}
              </svg>
            </button>
          </div>
        </div>

        {open ? (
          <div id="t3-mobile-panel" className="t3-mobile-panel">
            {categoryMenu ? <Theme03CategoryAccordion menu={categoryMenu} label={t('header.allCategories')} defaults={defaultImages} onNavigate={() => setOpen(false)} /> : null}
            <nav aria-label={t('header.categories')}>
              <ul>
                {items.map((item, index) => (
                  <li key={`m-${item.label}-${index}`}>
                    <Link href={menuItemHref(item, activeLanguage)} onClick={() => setOpen(false)}>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="t3-mobile-panel__meta t3-header__tools">
              <span className="t3-lang" style={{ display: 'flex' }} role="group" aria-label="Language">
                {LANGUAGES.map((language) => (
                  <Link
                    key={language.code}
                    href={languageHref(language.code)}
                    hrefLang={language.code}
                    aria-current={language.code === activeLanguage ? 'true' : undefined}
                    onClick={() => setOpen(false)}
                  >
                    {language.label}
                  </Link>
                ))}
              </span>
              <Link href={accountHref} onClick={() => setOpen(false)}>
                {t('header.account')}
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
